import { useEffect, useRef } from 'react';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useUserStore } from '../store/userStore';

const HEARTBEAT_INTERVAL_MS = 60_000; // write every 60 s

/**
 * Writes a heartbeat to /presence/{uid} once on mount and every 60 seconds
 * while the user is logged in. The platform admin dashboard uses this to show
 * active users in the last 5 minutes.
 */
export function usePresence() {
  const { sessionConfig } = useUserStore();
  const uid = sessionConfig?.userId;
  const entityId = sessionConfig?.entityId;
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!uid) return;

    const writeHeartbeat = () => {
      setDoc(
        doc(db, 'presence', uid),
        {
          userId: uid,
          entityId: entityId ?? null,
          lastSeen: serverTimestamp(),
        },
        { merge: true }
      ).catch(() => {
        // Non-critical — ignore failures silently
      });
    };

    writeHeartbeat();
    timerRef.current = setInterval(writeHeartbeat, HEARTBEAT_INTERVAL_MS);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [uid, entityId]);
}
